/**
 * `journey.id` — the one correlator that survives everything (§7, §11, §16.6).
 *
 * WHY IT EXISTS AT ALL. Two of the three reasons it was invented have since been fixed in code: the
 * Argo DAG is now one trace, and the queue hand-off is a deliberate trace boundary with a span link
 * rather than a lost one. What remains irreducible is the terraform client: `tofu`, CI and `curl`
 * call the state backend, the registry protocol and `PUT /plans/...tfplan`, and none of them will
 * ever send `traceparent`. So the correlator has to live in the DATA MODEL, not in context
 * propagation.
 *
 * TWO KINDS OF JOURNEY, and the distinction is the whole design:
 *
 *   A. Human-initiated in the browser (authentication, onboarding, policy.author,
 *      marketing.acquisition) - the SPA mints a UUID and keeps it in sessionStorage. The SPA is the
 *      sole writer.
 *   B. Machine-initiated or crossing into Kubernetes (module.publish, state.apply, plan.govern,
 *      provider.build, provider.scan) - the journey id IS the durable D1 row id the Worker is
 *      already creating. No new identifier, no new column, no new storage.
 *
 * (B) is the most useful decision in the schema: a journey is queryable in telemetry AND in D1 with
 * the same key, so `SELECT * FROM plan_versions WHERE id = '<journey.id>'` just works and an
 * operator debugging from either side lands in the same place.
 *
 * This module is SHARED between the browser and the Worker on purpose. The browser writes baggage
 * and the Worker parses it, across a public boundary - so the two halves of the validation rule must
 * be one piece of code, or they will disagree in exactly the way §16.6 warns about.
 */
import { JOURNEY_NAMES, isMember, type JourneyName } from './vocab.js'

export interface Journey {
  id: string
  name: JourneyName
}

/** §7: an A-type journey is abandoned after 30 minutes idle rather than resumed. */
export const JOURNEY_IDLE_TIMEOUT_MS = 30 * 60 * 1000

/** sessionStorage key prefix for A-type journeys. */
export const JOURNEY_KEY_PREFIX = 'tm_journey_'

export const journeyStorageKey = (name: JourneyName): string => `${JOURNEY_KEY_PREFIX}${name}`

// ─── Baggage (§11) ───────────────────────────────────────────────────────────────────────────────

/**
 * §11 caps baggage length. Picked well under the 8192-byte W3C limit: we write exactly two
 * entries, so anything longer is either an attack or a bug, and both deserve to be dropped.
 */
export const MAX_BAGGAGE_LENGTH = 256

/** Longest `journey.id` accepted. A UUID is 36; a D1 row id is a UUID. 64 leaves room and no more. */
const MAX_JOURNEY_ID_LENGTH = 64

/**
 * A UUID, or something UUID-shaped.
 *
 * Deliberately not a strict RFC-4122 version check: §7 says a B-type journey id is "an existing
 * durable D1 row id", and those are generated by `crypto.randomUUID()` today but the rule is about
 * provenance, not about version nibbles. Shape plus length is the right amount of validation - it
 * rejects an injected query fragment or a 2KB blob without rejecting a legitimate row id.
 */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isValidJourneyId(value: unknown): value is string {
  return typeof value === 'string' && value.length <= MAX_JOURNEY_ID_LENGTH && UUID_SHAPE.test(value)
}

/**
 * Encode a journey as a W3C `baggage` header value.
 *
 * `baggage` and not `tracestate`: baggage is designed for application-level values, tracestate is
 * for sampling and vendor state (§11). And not a proprietary `X-Terramantle-Journey` header, because
 * the standard propagators already handle this one.
 */
export function encodeJourneyBaggage(journey: Journey): string {
  return `journey.id=${encodeURIComponent(journey.id)},journey.name=${encodeURIComponent(journey.name)}`
}

/**
 * Parse a `baggage` header into a journey — treating it as HOSTILE input (§16.6).
 *
 * "Baggage is attacker-controlled on a public endpoint, so the Worker must treat it as a hint only."
 * Concretely, this function:
 *
 *   - returns `null` rather than throwing, for anything malformed;
 *   - caps the whole header, so a megabyte of baggage costs one length check;
 *   - requires `journey.id` to be UUID-shaped, so it cannot carry a LogQL fragment, a path, or a
 *     2KB string into an attribute that lands in a log store;
 *   - requires `journey.name` to be a member of the closed enum, so it cannot inflate the
 *     cardinality of an attribute that dashboards use as a variable;
 *   - IGNORES every other baggage entry. Never `user.id`, never `tenant.id`, never a token: those
 *     are derived from the validated bearer token and must never come from a header the caller
 *     controls.
 */
export function parseJourneyBaggage(header: string | null | undefined): Journey | null {
  if (!header || header.length > MAX_BAGGAGE_LENGTH) return null
  let id: string | undefined
  let name: string | undefined
  for (const part of header.split(',')) {
    // A baggage entry may carry `;`-delimited properties; W3C says ignore what you do not know.
    const [pair] = part.split(';')
    const eq = (pair ?? '').indexOf('=')
    if (eq <= 0) continue
    const key = (pair ?? '').slice(0, eq).trim()
    const raw = (pair ?? '').slice(eq + 1).trim()
    let value: string
    try {
      value = decodeURIComponent(raw)
    } catch {
      continue // a lone `%` is not a reason to drop the whole header
    }
    if (key === 'journey.id') id = value
    else if (key === 'journey.name') name = value
  }
  if (!isValidJourneyId(id)) return null
  if (!isMember(JOURNEY_NAMES, name)) return null
  return { id, name }
}

// ─── Browser-side lifecycle (§7 case A) ──────────────────────────────────────────────────────────

/**
 * A minimal storage interface, so this works against `sessionStorage`, a test double, or nothing at
 * all. Storage access in the browser is DEFENSIVE by contract: incognito and blocked site-data must
 * not throw, which is already true of the existing `tm_sid`/`tm_uid` helpers.
 */
export interface JourneyStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

interface StoredJourney {
  id: string
  at: number
}

/**
 * Read the live journey for `name`, discarding one that has gone stale.
 *
 * §7: an A-type journey "must be treated as abandoned after 30 minutes idle (the SPA stores a
 * timestamp alongside the id and discards a stale one rather than resuming it)". Resuming a stale
 * journey would be worse than minting a new one: it would stitch two unrelated sessions into one
 * query result.
 */
export function readJourney(
  store: JourneyStore | undefined,
  name: JourneyName,
  now: number = Date.now(),
): Journey | null {
  if (!store) return null
  try {
    const raw = store.getItem(journeyStorageKey(name))
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredJourney
    if (!isValidJourneyId(parsed?.id)) return null
    if (typeof parsed.at !== 'number' || now - parsed.at > JOURNEY_IDLE_TIMEOUT_MS) {
      store.removeItem(journeyStorageKey(name))
      return null
    }
    return { id: parsed.id, name }
  } catch {
    return null
  }
}

/** Mint a journey, or refresh the idle timer on the live one. Never throws. */
export function startJourney(
  store: JourneyStore | undefined,
  name: JourneyName,
  newId: () => string,
  now: number = Date.now(),
): Journey {
  const existing = readJourney(store, name, now)
  const journey: Journey = existing ?? { id: newId(), name }
  touchJourney(store, journey, now)
  return journey
}

/** Push the idle deadline out. Called on every emit for that journey. */
export function touchJourney(
  store: JourneyStore | undefined,
  journey: Journey,
  now: number = Date.now(),
): void {
  try {
    store?.setItem(journeyStorageKey(journey.name), JSON.stringify({ id: journey.id, at: now }))
  } catch {
    /* blocked storage is not an error */
  }
}

/** End a journey at its completion or failure event (§7). */
export function endJourney(store: JourneyStore | undefined, name: JourneyName): void {
  try {
    store?.removeItem(journeyStorageKey(name))
  } catch {
    /* ignore */
  }
}

/**
 * Clear EVERY journey key.
 *
 * §7: "`resetTelemetryIdentity()` at logout must clear every `tm_journey_*` key alongside
 * `tm_sid`/`tm_uid`/`tm_hc`." Iterating the known enum rather than scanning the store keeps this
 * correct when storage enumeration is unavailable, and means a renamed journey cannot leave an
 * orphan behind.
 */
export function clearAllJourneys(store: JourneyStore | undefined): void {
  for (const name of JOURNEY_NAMES) endJourney(store, name)
}
